import {
  ENTIRE_FRANCE_DISTANCE_MAX,
  SEARCH_COUNT_PER_PAGE,
} from '@/src/constants';

type Filters = {
  address?: {
    type: 'street' | 'housenumber' | string;
    longitude?: string;
    latitude?: string;
    label?: string;
    zipCode?: string;
    shortName?: string;
    [k: string]: any;
  } | null;
  speciality?: string;
  extraFeeFilter?: string;
  thirdPartyFilter?: string;
};
type QueryParams = {
  longitude?: string;
  latitude?: string;
  locality?: string;
  street?: string;
  speciality?: string;
  geoPermission?: string;
  slug?: string[] | string;
};
type ReduceFunction<
  T1 extends Record<string, any>,
  T2 extends Record<string, any>,
  K extends keyof T1
> = (current: T2, key: K, value: T1[K], obj: T1) => T2;
type ReduceFns<
  T1 extends Record<string, any>,
  T2 extends Record<string, any>
> = Partial<
  {
    [K in keyof T1]: ReduceFunction<T1, T2, K>;
  }
> & { _default?: ReduceFunction<T1, T2, keyof T1> };

export const defaultFilters = {
  page: '0',
  distanceMax: ENTIRE_FRANCE_DISTANCE_MAX.toString(),
  limit: SEARCH_COUNT_PER_PAGE.toString(),
};

const cleanedObject = (obj: Record<string, any>) =>
  Object.keys(obj).reduce(
    (acc, key) =>
      obj[key] === undefined ? { ...acc } : { ...acc, [key]: obj[key] },
    {},
  );

/*
 We have 3 transformer
 * filter state -> url params (for beautiful url)
 * url params -> search api params (for call api)
 * url params -> filter state (for SSR)

 */
// Transform filter state -> url params
const reduceUrlParamsFn: ReduceFns<Filters, QueryParams> = {
  address: (current, _key, value) => {
    if (!value) return current;
    const { type } = value;
    const addressParams: Record<string, any> = {};
    if (['street', 'housenumber'].indexOf(type) >= 0) {
      const { longitude, latitude, label } = value;
      addressParams.longitude = longitude;
      addressParams.latitude = latitude;
      addressParams.locality = undefined;
      addressParams.street = label;
    } else {
      const { shortName, zipCode, longitude, latitude } = value;
      addressParams.locality = `${zipCode}-${shortName}`;
      addressParams.longitude = longitude;
      addressParams.latitude = latitude;
    }
    return { ...current, ...addressParams };
  },
  _default: (current, key, value) =>
    value !== '' ? { ...current, [key]: value } : current,
};

// Transform url params -> search api params
const reduceSearchFiltersFn: ReduceFns<
  QueryParams,
  Record<string, string | undefined>
> = {
  speciality: (current, _key, value) => ({
    ...current,
    'speciality.shortName': value !== 'specialities' ? value : undefined,
  }),
  street: current => current,
  geoPermission: current => current,
  _default: (current, key, value) =>
    typeof value === 'string' && value !== ''
      ? { ...current, [key]: value }
      : current,
};

// Transform url params -> filter state
const reduceInitialFiltersFn: ReduceFns<QueryParams, Filters> = {
  locality: (current, _key, value) => {
    if (!value) return current;
    const [zipCode, ...cityParts] = value.split('-'); // pass limit to split function not work
    const city = cityParts.join('-');
    return {
      ...current,
      address: {
        type: 'municipality',
        label: `${city} (${zipCode})`,
        zipCode,
        city,
        shortName: city,
      },
    };
  },
  street: (current, _key, value, obj) => {
    const { longitude, latitude } = obj;
    return {
      ...current,
      address: {
        label: value,
        longitude,
        latitude,
        type: 'street',
      },
    };
  },
  longitude: current => current,
  latitude: current => current,
  _default: (current, key, value) =>
    value !== '' ? { ...current, [key]: value } : current,
};

// Helper function for transformer
const applyReduce = <
  T1 extends Record<string, any>,
  T2 extends Record<string, any>
>(
  obj: T1,
  fns: ReduceFns<T1, T2>,
) =>
  Object.keys(obj).reduce((result, key) => {
    const value = obj[key];
    const _applyFn = fns?.[key] ?? fns._default;
    if (!_applyFn) return result;
    return _applyFn(result, key, value, obj);
  }, {} as T2);

const cleanFiltersQuery = (query: QueryParams): QueryParams => {
  const { slug, ...restQuery } = query;
  /**
   * endpoint returns no results if latitude or longitude are 0
   * lat 0 && long 0 is is the ocean so it is safe to not allow it
   * however, if we find the source of the problem, we should fix it
   *  */
  const hasLatLong =
    query.latitude &&
    query.longitude &&
    query.latitude !== '0' &&
    query.longitude !== '0';

  const result: QueryParams = {
    ...cleanedObject({
      ...defaultFilters,
      ...restQuery,
      // eslint-disable-next-line no-nested-ternary
      ...(slug?.length === 2
        ? { speciality: query.slug?.[0], locality: query.slug?.[1] }
        : slug?.length === 1
        ? { speciality: query.slug?.[0] }
        : {}),
    }),
    latitude: hasLatLong ? query.latitude : undefined,
    longitude: hasLatLong ? query.longitude : undefined,
  };

  return result;
};

export const queryParams2ApiParams = (query: QueryParams) =>
  applyReduce(cleanFiltersQuery(query), reduceSearchFiltersFn);

export const queryParams2InitialFilters = (query: QueryParams) =>
  applyReduce(cleanFiltersQuery(query), reduceInitialFiltersFn);

export const filters2QueryParams = (filters: Filters) =>
  applyReduce(filters, reduceUrlParamsFn);
